import type { ActionButton } from '@app/src/components/ActionButtons'
import { ProvidersModalPurpose, useProvidersModal } from '@app/src/context/ProvidersModalContext'
import useCurrentProviderType from '@app/src/hooks/currentProviderType'
import { useWriteAccess } from '@app/src/hooks/useWriteAccess'
import { OrganizationDetailsGlanceCard } from '@app/src/pages/ResourceCollection/Collections/Provider/ConnectProviders/OrganizationDetailsGlanceCard'
import type { GenericOrganization, Provider } from '@app/src/types/organizations'
import type { ReferralContact } from '@app/src/types/resourceExplorer'
import { insertIf } from '@app/src/utils/helpersTs'
import { ChevronRight, MailOutline } from '@mui/icons-material'
import { Box, Divider, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import { useDrawer } from '../DrawerContext'
import DrawerView from '../DrawerView'
import DrawerViewAddOrganizationAsProvider from './DrawerViewAddOrganizationAsProvider'

interface DrawerViewLinkProviderCandidatesProps {
  resource: GenericOrganization
  organizationCandidates?: Provider[]
  referralContact?: ReferralContact
  hideNewCompany?: boolean
  showBackButton?: boolean
}

const DrawerViewLinkProviderCandidates: React.FC<DrawerViewLinkProviderCandidatesProps> = ({
  resource,
  organizationCandidates,
  hideNewCompany,
  showBackButton,
}) => {
  const { openDrawer, closeDrawer } = useDrawer()
  const resourceType = useCurrentProviderType()
  const { formatMessage } = useIntl()
  const { handleOpenConnectDialog } = useProvidersModal()
  const hasWriteAccess = useWriteAccess()

  return (
    <DrawerView
      title={formatMessage({ id: 'general.connect' })}
      subTitle={resource.name}
      showBackButton={showBackButton}
      buttons={[
        ...insertIf<ActionButton>(!hideNewCompany && hasWriteAccess, {
          label: formatMessage({ id: 'referral.invitation.newCompany' }),
          startIcon: <MailOutline />,
          variant: 'outlined',
          color: 'primary',
          onClick: () => {
            handleOpenConnectDialog([resource], ProvidersModalPurpose.InitialInvitation)
            closeDrawer()
          },
        }),
      ]}
      stackButtons
    >
      <Stack spacing={2}>
        <Box px={3} pt={4}>
          <Typography variant="body1">
            {formatMessage({ id: 'referral.invitation.orgsFoundTitle' }, { count: organizationCandidates?.length })}
          </Typography>
        </Box>

        <Stack>
          {organizationCandidates
            ?.filter(candidate => !candidate.alreadyLinkedOrganization)
            ?.map(candidate => (
              <React.Fragment key={candidate.id}>
                <Divider />
                <ListItem dense disablePadding>
                  <ListItemButton
                    onClick={() =>
                      openDrawer(
                        <DrawerViewAddOrganizationAsProvider
                          provider={candidate}
                          resource={resource}
                          providerType={resourceType}
                        />,
                        { showAboveDialogs: true },
                      )
                    }
                    sx={{ pl: 3, pb: 1 }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" sx={{ pb: 0.5 }}>
                          {candidate.name}
                        </Typography>
                      }
                      secondary={<OrganizationDetailsGlanceCard company={candidate} />}
                    />
                    <ListItemIcon sx={{ minWidth: 'auto', marginLeft: 0 }}>
                      <ChevronRight />
                    </ListItemIcon>
                  </ListItemButton>
                </ListItem>
              </React.Fragment>
            ))}
        </Stack>
      </Stack>
    </DrawerView>
  )
}

export default DrawerViewLinkProviderCandidates
